/* eslint-disable react/require-default-props */
import React from 'react';
import { Controller } from 'react-hook-form';
import TextInput from '@components/TextInput/TextInput';
import Select from '@components/Select/Select';
import InputTextArea, { StyleVariant } from '@components/InputTextArea/InputTextArea';
import DateInput from '@components/DateInput/DateInput';
import CheckboxList from '@components/v2/CheckboxList/CheckboxList';
import { truncateNumber, getNumberMaxLength } from '@utils/functions/number.functions';
import { translate } from '@utils/i18n';
const namespace = 'components.dynamic_settings';
export default function DynamicSettings({ fieldDefinitions, control }) {
    if (!fieldDefinitions || fieldDefinitions.length === 0) {
        return React.createElement("div", null);
    }
    const renderField = (field) => {
        const fieldKey = `settings.${field.key}`;
        switch (field.type) {
            case 'text':
                return (React.createElement("div", { key: field.key, className: "form-row" },
                    React.createElement(Controller, { name: fieldKey, control: control, defaultValue: field.default !== undefined ? field.default : undefined, rules: {
                            required: field.required ? translate('field_required', { namespace, field: field.label }) : false,
                            maxLength: field.maxLength
                                ? {
                                    value: field.maxLength,
                                    message: translate('field_max_length', { namespace, field: field.label, count: field.maxLength })
                                }
                                : undefined
                        }, render: ({ field: controllerField, fieldState }) => {
                            var _a;
                            return (React.createElement(TextInput, { value: controllerField.value || '', name: controllerField.name, onChange: controllerField.onChange, onBlur: controllerField.onBlur, label: field.label, error: (_a = fieldState.error) === null || _a === void 0 ? void 0 : _a.message, required: field.required, maxLength: field.maxLength, placeholder: field.placeholder }));
                        } })));
            case 'textarea':
                return (React.createElement("div", { key: field.key, className: "form-row" },
                    React.createElement(Controller, { name: fieldKey, control: control, defaultValue: field.default !== undefined ? field.default : undefined, rules: {
                            required: field.required ? translate('field_required', { namespace, field: field.label }) : false,
                            maxLength: field.maxLength
                                ? {
                                    value: field.maxLength,
                                    message: translate('field_max_length', { namespace, field: field.label, count: field.maxLength })
                                }
                                : undefined
                        }, render: ({ field: controllerField }) => (React.createElement(InputTextArea
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        , Object.assign({}, controllerField, { value: controllerField.value || '', label: field.label, variant: StyleVariant.Light, buttonControlled: false }))) })));
            case 'number':
                return (React.createElement("div", { key: field.key, className: "form-row" },
                    React.createElement(Controller, { name: fieldKey, control: control, defaultValue: field.default !== undefined ? field.default : undefined, rules: {
                            required: field.required ? translate('field_required', { namespace, field: field.label }) : false,
                            min: field.min !== undefined
                                ? {
                                    value: field.min,
                                    message: translate('field_min', { namespace, field: field.label, count: field.min })
                                }
                                : undefined,
                            max: field.max !== undefined
                                ? {
                                    value: field.max,
                                    message: translate('field_max', { namespace, field: field.label, count: field.max })
                                }
                                : undefined
                        }, render: ({ field: controllerField, fieldState }) => {
                            var _a, _b, _c, _d;
                            return (React.createElement(React.Fragment, null,
                                React.createElement(TextInput
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                , Object.assign({}, controllerField, { label: field.label, type: "number", min: (_a = field.min) === null || _a === void 0 ? void 0 : _a.toString(), max: (_b = field.max) === null || _b === void 0 ? void 0 : _b.toString(), step: "1", placeholder: field.placeholder, onChange: (e) => {
                                        const { value } = e.target;
                                        const boundedValue = truncateNumber(value, getNumberMaxLength(field.min, field.max));
                                        const parsed = boundedValue ? parseInt(boundedValue, 10) : null;
                                        controllerField.onChange(Number.isNaN(parsed) ? null : parsed);
                                    }, value: ((_c = controllerField.value) === null || _c === void 0 ? void 0 : _c.toString()) || '', error: (_d = fieldState.error) === null || _d === void 0 ? void 0 : _d.message, required: field.required })),
                                field.unit && React.createElement("span", { className: "field-unit" },
                                    "(",
                                    field.unit,
                                    ")")));
                        } })));
            case 'select':
                return (React.createElement("div", { key: field.key, className: "form-row" },
                    React.createElement(Controller, { name: fieldKey, control: control, defaultValue: field.default !== undefined ? field.default : undefined, rules: {
                            required: field.required ? translate('field_required', { namespace, field: field.label }) : false,
                            validate: (value) => {
                                // Ensure empty string is treated as invalid for required fields
                                if (field.required && (!value || value === '')) {
                                    return translate('field_required', { namespace, field: field.label });
                                }
                                return true;
                            }
                        }, render: ({ field: controllerField, fieldState }) => {
                            var _a;
                            const options = (field.options || []).map((option, index) => {
                                var _a;
                                return ({
                                    label: ((_a = field.optionsLocalized) === null || _a === void 0 ? void 0 : _a[index]) || option,
                                    value: option
                                });
                            });
                            return (React.createElement(Select, { id: field.key, name: controllerField.name, label: field.label, options: options, required: field.required, error: (_a = fieldState.error) === null || _a === void 0 ? void 0 : _a.message, value: controllerField.value || '', onChange: (e) => {
                                    controllerField.onChange(e);
                                }, onBlur: controllerField.onBlur }));
                        } })));
            case 'multiselect':
                return (React.createElement("div", { key: field.key, className: "form-row" },
                    React.createElement(Controller, { name: fieldKey, control: control, defaultValue: Array.isArray(field.default) ? field.default : [], rules: {
                            required: field.required ? translate('field_required', { namespace, field: field.label }) : false,
                            validate: (value) => {
                                // Ensure at least one option is selected for required fields
                                if (field.required && (!value || !Array.isArray(value) || value.length === 0)) {
                                    return translate('field_required', { namespace, field: field.label });
                                }
                                return true;
                            }
                        }, render: ({ field: controllerField, fieldState }) => {
                            var _a;
                            const options = (field.options || []).map((option, index) => {
                                var _a;
                                return ({
                                    title: ((_a = field.optionsLocalized) === null || _a === void 0 ? void 0 : _a[index]) || option,
                                    value: option
                                });
                            });
                            let currentValues = [];
                            if (Array.isArray(controllerField.value)) {
                                currentValues = controllerField.value;
                            }
                            else if (Array.isArray(field.default)) {
                                currentValues = field.default;
                            }
                            return (React.createElement(CheckboxList, { label: field.label, options: options, value: currentValues, required: field.required, error: (_a = fieldState.error) === null || _a === void 0 ? void 0 : _a.message, onChange: (toggledValue) => {
                                    const newValues = currentValues.includes(toggledValue)
                                        ? currentValues.filter((v) => v !== toggledValue)
                                        : [...currentValues, toggledValue];
                                    controllerField.onChange(newValues);
                                } }));
                        } })));
            case 'date': {
                // Convert default value to Date if it's a string or number
                let defaultDate;
                if (field.default !== undefined) {
                    if (typeof field.default === 'string' || typeof field.default === 'number') {
                        defaultDate = new Date(field.default);
                    }
                    else if (field.default instanceof Date) {
                        defaultDate = field.default;
                    }
                }
                return (React.createElement("div", { key: field.key, className: "form-row" },
                    React.createElement(Controller, { name: fieldKey, control: control, defaultValue: defaultDate, rules: {
                            required: field.required ? translate('field_required', { namespace, field: field.label }) : false
                        }, render: ({ field: controllerField }) => (React.createElement(DateInput, { id: field.key, value: controllerField.value || null, onChange: (date) => {
                                controllerField.onChange(date);
                            }, label: field.label, name: controllerField.name, required: field.required })) })));
            }
            case 'boolean':
                return (React.createElement("div", { key: field.key, className: "form-row" },
                    React.createElement(Controller, { name: fieldKey, control: control, defaultValue: field.default !== undefined ? field.default : undefined, render: ({ field: controllerField }) => (React.createElement(CheckboxList, { label: field.label, options: [{ title: field.label, value: 'true' }], value: controllerField.value ? ['true'] : [], onChange: () => {
                                // Toggle the boolean value based on checkbox selection
                                const newValue = controllerField.value ? [] : ['true'];
                                const booleanValue = newValue.includes('true');
                                controllerField.onChange(booleanValue);
                            } })) })));
            default:
                return (React.createElement("div", { key: field.key, className: "form-row" },
                    "Unsupported field type: ",
                    field.type));
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "form-row" },
            React.createElement("span", null,
                React.createElement("b", null, "Component-specific fields"))),
        fieldDefinitions.map((field) => renderField(field))));
}
